import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { RationWorkshop } from '../../components/ration-workshop';
import { Skeleton } from '../../components/ui/skeleton';
import { useMediaQuery } from '../../hooks/use-media-query';
import { currentRationsQuery, inventoryQuery, sessionQuery } from '../../lib/queries';

export const Route = createFileRoute('/_auth/ration')({
  component: RationViewPage,
});

function RationViewPage() {
  const { data: session } = useQuery(sessionQuery);
  const { data, isLoading } = useQuery(currentRationsQuery);
  const { data: inventoryData } = useQuery(inventoryQuery());
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const GROUP_LABELS: Record<string, string> = {
    milking: 'Milking',
    dry: 'Dry',
    heifers: 'Heifers',
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg px-4 py-4 space-y-4">
        <h1 className="text-2xl font-bold">Current Ration</h1>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const rations = data?.rations ?? [];
  const inventory = inventoryData?.items ?? [];

  // Desktop farmer: show ration workshop
  if (isDesktop && session?.role === 'farmer' && rations.length > 0) {
    return (
      <div className="space-y-4 max-w-5xl">
        <h1 className="text-2xl font-bold">Ration Workshop</h1>
        <RationWorkshop rations={rations} inventory={inventory} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-4 space-y-4">
      <h1 className="text-2xl font-bold">Current Ration</h1>

      {rations.length === 0 ? (
        <p className="py-8 text-center text-ndd-400">No rations configured yet.</p>
      ) : (
        rations.map((ration) => (
          <div key={ration.ration_id} className="rounded-lg bg-ndd-900 overflow-hidden">
            {/* Group header */}
            <div className="px-4 py-3 border-b border-ndd-800">
              <h2 className="font-semibold">
                {GROUP_LABELS[ration.group_id] ?? ration.group_id}
                {ration.head_count_at_creation != null && (
                  <span className="ml-2 text-ndd-400 font-normal">
                    · {ration.head_count_at_creation} head
                  </span>
                )}
              </h2>
            </div>

            {/* Ingredients */}
            <div className="px-4 py-2 space-y-1.5">
              {ration.ingredients.map((ing) => (
                <div key={ing.name} className="flex justify-between text-sm font-mono">
                  <span className="text-ndd-100">{ing.name}</span>
                  <span className="text-ndd-300 tabular-nums">
                    {ing.lbs_per_head_day.toFixed(1)} lbs/hd
                  </span>
                </div>
              ))}
            </div>

            {/* Separator */}
            <div className="mx-4 h-px bg-ndd-800" />

            {/* Totals */}
            <div className="px-4 py-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-ndd-400">Total lbs/head</span>
                <span className="font-semibold tabular-nums">
                  {ration.total_lbs_per_head?.toFixed(1) ?? '—'}
                </span>
              </div>
              {ration.total_batch_weight_lbs != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-ndd-400">Batch ({ration.batch_hours}hr)</span>
                  <span className="font-semibold tabular-nums">
                    {Math.round(ration.total_batch_weight_lbs).toLocaleString()} lbs
                  </span>
                </div>
              )}
              <p className="text-xs text-ndd-500 mt-1">Since {ration.effective_date}</p>
            </div>
          </div>
        ))
      )}

      {session?.role === 'farmer' && (
        <p className="text-center text-sm text-ndd-500 py-2">Edit rations on desktop</p>
      )}
    </div>
  );
}
